import { unixToDate } from "@/lib/utils";
import * as Dialog from "@radix-ui/react-dialog";

interface AttendeeDetailsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCloseChange?: () => void;
  attendee: {
    full_name: string;
    email: string;
    company: string;
    job_title: string;
    ticket_type: string;
    registration_date: number;
    payment_status: string;
  };
}

export function AttendeeDetailsModal({
  open,
  onOpenChange,
  onCloseChange,
  attendee,
}: AttendeeDetailsModalProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-white rounded-lg shadow-xl focus:outline-none overflow-hidden">
          <div className="bg-gradient-to-r from-blue-500 to-primary p-6 text-white">
            <div className="flex justify-between items-start">
              <div>
                <Dialog.Title className="text-2xl font-bold capitalize">
                  {attendee.full_name}
                </Dialog.Title>
                <Dialog.Description className="text-blue-100 mt-1">
                  Attendee Details
                </Dialog.Description>
              </div>
              <Dialog.Close asChild>
                <button
                  onClick={() => {
                    if (onCloseChange) onCloseChange();
                  }}
                  className="text-white hover:text-blue-200 transition-colors"
                >
                  X
                </button>
              </Dialog.Close>
            </div>
          </div>

          <div className="p-6 space-y-4">
            <DetailItem label="Email" value={attendee.email} />
            <DetailItem
              label="Ticket Type"
              value={attendee.ticket_type}
              capitalize
            />
            <DetailItem
              label="Registration Date"
              value={unixToDate(attendee.registration_date)}
            />

            <div className="flex items-center gap-4">
              <DetailItem
                label="Company"
                value={attendee.company || "Not provided"}
              />
              <DetailItem
                label="Job Title"
                value={attendee.job_title || "Not provided"}
              />
            </div>

            <div className="pt-2">
              <span className="text-sm font-medium text-gray-500">
                Payment Status
              </span>
              <div className="mt-1">
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    attendee.payment_status === "confirmed"
                      ? "bg-green-100 text-green-800"
                      : "bg-yellow-100 text-yellow-800"
                  }`}
                >
                  {attendee.payment_status}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 px-6 py-4 flex justify-end">
            <Dialog.Close asChild>
              <button className="px-4 py-2 bg-primary hover:bg-blue-700 text-white rounded-md transition-colors">
                Close
              </button>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function DetailItem({
  label,
  value,
  capitalize = false,
}: {
  label: string;
  value: string;
  capitalize?: boolean;
}) {
  return (
    <div>
      <span className="text-sm font-medium text-gray-500">{label}</span>
      <p className={`mt-1 text-gray-900 ${capitalize ? "capitalize" : ""}`}>
        {value}
      </p>
    </div>
  );
}
